
import { defineComponent, PropType, computed } from 'vue'
import { requiredRule } from '@/modules/validations'
import moment from 'moment'
import FormFieldScheduledPrice from '@/components/_shared/form-fields/scheduled-price.vue'
import { AgreementScheduledPrice } from '@/modules/agreements'
import QForm from 'quasar/src/components/form/QForm.js';

export interface LocalScheduledPrice {
    id: string
    validFrom: string
    price: number
    delete: boolean
    updated: boolean
}

export default defineComponent({
    name: 'form-field-scheduled-prices',
    components: { FormFieldScheduledPrice },
    props: {
        modelValue: {
            type: Array as PropType<LocalScheduledPrice[]>,
            required: true
        },
        agreementId: {
            type: String,
            required: true
        },
        createPrices: {
            type: Function as PropType<(updatedPrices: AgreementScheduledPrice[]) => void>,
            required: true
        },
        updatePrices: {
            type: Function as PropType<(updatedPrices: AgreementScheduledPrice[]) => void>,
            required: true
        },
        deletePrices: {
            type: Function as PropType<(updatedPrices: AgreementScheduledPrice[]) => void>,
            required: true
        },
        onSubmitted: {
            type: Function as PropType<() => void>,
            required: true
        },
        onCancelClick: {
            type: Function as PropType<() => void>,
            required: true
        }
    },
    watch: {
        localScheduledPrices: {
            handler: 'updatePricesOnChange',
            deep: true
        }
    },
    setup (props, { emit }) {
        const localModel = computed({
            get: () => props.modelValue,
            set: value => emit('update:modelValue', value)
        })

        return {
            requiredRule,
            localModel,
            getDateOptions (date: string) {
                const tomorrow = moment().add(1, 'day').format('YYYY/MM/DD')
                return date >= tomorrow
            }
        }
    },
    data () {
        return {
            localScheduledPrices: this.modelValue.map((price) => ({
                ...price,
                price: price.price * 0.01,
                updated: false,
                delete: false
            })) as LocalScheduledPrice[] // Create a local copy with additional fields
        }
    },
    methods: {
        removePrice (index: number) {
            if (this.localScheduledPrices[index].id === '') {
                this.localScheduledPrices.splice(index, 1)
            } else {
                this.localScheduledPrices[index].delete = true
            }
        },
        addPrice () {
            this.localScheduledPrices.push({
                id: '',
                validFrom: moment().add(1, 'month').startOf('month').format('YYYY-MM-DD'),
                price: 0,
                delete: false,
                updated: true
            })
        },
        mapLocalPricesToModel (prices: LocalScheduledPrice[]) {
            return prices.map((price) => ({
                id: price.id,
                agreementId: this.agreementId,
                price: price.price * 100,
                validFrom: price.validFrom
            })) as AgreementScheduledPrice[]
        },
        async revalidateForm () {
            const form = this.$refs.scheduledPricesForm as QForm
            return await form.validate()
        },
        async submitPrices () {
            const isValid = await this.revalidateForm()
            if (!isValid) {
                return
            }

            const createdPrices = this.localScheduledPrices.filter(
                (price) => price.id === ''
            )
            await this.createPrices(this.mapLocalPricesToModel(createdPrices))

            const updatedPrices = this.localScheduledPrices.filter(
                (price) => !price.delete && price.updated && price.id !== ''
            )
            await this.updatePrices(this.mapLocalPricesToModel(updatedPrices))

            const removedPrices = this.localScheduledPrices.filter(
                (price) => price.delete
            )
            await this.deletePrices(this.mapLocalPricesToModel(removedPrices))
            this.onSubmitted()
        },
        updatePricesOnChange (newVal: LocalScheduledPrice[]) {
            newVal.forEach((price, index) => {
                if (price.id === '') {
                    return
                }
                const isPriceUpdated = (price.price * 100) !== this.modelValue[index].price
                const isValidFromUpdated = price.validFrom !== this.modelValue[index].validFrom
                price.updated = isPriceUpdated || isValidFromUpdated
            })
        }
    }
})
